import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MessageCircle, Pencil, Plus, GitBranch, UserPlus, Link2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatWIB } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { ProspectForm } from "@/components/ProspectForm";
import { StatusChangeModal } from "@/components/StatusChangeModal";
import { ConvertModal } from "@/components/ConvertModal";
import { CHANNELS, FU_STATUS, FU_STATUS_LABELS, waLink, wibLocalToUtcIso, statusLabel, PIPELINE_STATUS_CLS } from "@/lib/prospectMeta";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";

function Row({ label, value }) {
  if (!value) return null;
  return <div className="py-1.5 border-b border-slate-100 last:border-0"><p className="text-xs text-slate-400">{label}</p><p className="text-sm text-slate-800">{value}</p></div>;
}

export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [fu, setFu] = useState({ scheduled_at: "", channel: "WhatsApp", outcome: "", notes: "", next_follow_up_at: "", status: "terjadwal", pic_user_id: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/prospects/${id}`); setP(data); }
    catch { toast.error("Prospek tidak ditemukan."); navigate("/prospek"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/prospects/meta").then(({ data }) => setMembers(data.members)).catch(() => {}); }, []);
  useEffect(() => { if (user?.id) setFu((prev) => prev.pic_user_id ? prev : { ...prev, pic_user_id: user.id }); }, [user]);

  const addFu = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/prospects/${id}/followups`, {
        ...fu,
        scheduled_at: wibLocalToUtcIso(fu.scheduled_at),
        next_follow_up_at: wibLocalToUtcIso(fu.next_follow_up_at),
      });
      toast.success("Catatan follow-up ditambahkan.");
      setFu({ scheduled_at: "", channel: "WhatsApp", outcome: "", notes: "", next_follow_up_at: "", status: "terjadwal", pic_user_id: user?.id || "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal menyimpan."); }
    finally { setSaving(false); }
  };

  if (loading || !p) return <AppLayout><div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div></AppLayout>;
  const isL = p.prospect_type === "lembaga";
  const name = p.institution_name || p.full_name;

  return (
    <AppLayout>
      <button onClick={() => navigate("/prospek")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="detail-back"><ArrowLeft className="h-4 w-4" /> Kembali ke Prospek</button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-xl font-bold text-slate-900" data-testid="detail-name">{name}</h1>
                <p className="text-sm text-slate-500">{isL ? "Lembaga / Corporate" : "Individu"} · {p.category}</p>
                {p.converted && (
                  <button onClick={() => navigate(p.conversion_type === "client" ? `/klien/${p.conversion_id}` : `/siswa/${p.conversion_id}`)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full hover:bg-green-100" data-testid="detail-converted-badge">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Sudah Dikonversi · {p.conversion_type === "client" ? "Lihat Data Klien" : "Lihat Data Siswa"}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <a href={waLink(p.phone_normalized)} target="_blank" rel="noreferrer" data-testid="detail-wa-button" className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-2 rounded-lg"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
                <button onClick={() => setShowStatus(true)} data-testid="detail-status-button" className="inline-flex items-center gap-1.5 bg-[#f97316] hover:bg-[#ea580c] text-white text-sm font-medium px-3 py-2 rounded-lg"><GitBranch className="h-4 w-4" /> Ubah Status</button>
                {p.status === "berhasil" && !p.converted && (
                  <button onClick={() => setShowConvert(true)} data-testid="detail-convert-button" className="inline-flex items-center gap-1.5 bg-[#0f2847] hover:bg-[#17416f] text-white text-sm font-medium px-3 py-2 rounded-lg"><UserPlus className="h-4 w-4" /> Konversi</button>
                )}
                <button onClick={() => setShowEdit(true)} data-testid="detail-edit-button" className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-slate-50"><Pencil className="h-4 w-4" /> Edit</button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <Row label="WhatsApp" value={p.phone_normalized} />
              <Row label="Email" value={p.email} />
              {!isL && <Row label="Nama Orang Tua/Wali" value={p.guardian_name} />}
              {isL && <Row label="Kontak/PIC" value={p.contact_person} />}
              {isL && <Row label="Jabatan" value={p.position} />}
              {!isL && <Row label="Sekolah/Univ/Perusahaan" value={p.school_or_company} />}
              {!isL && <Row label="Kelas/Pekerjaan" value={p.class_or_job} />}
              <Row label="Alamat" value={p.address} />
              <Row label="Program" value={p.program_interest} />
              {!isL && <Row label="Tujuan Belajar" value={p.learning_goal} />}
              {isL && <Row label="Jumlah Calon Peserta" value={p.participant_count} />}
              <Row label="Mode" value={p.mode} />
              <Row label={isL ? "Rencana Pelaksanaan" : "Jadwal Diinginkan"} value={isL ? p.implementation_plan : p.desired_schedule} />
              <Row label="Sumber" value={p.source} />
              <Row label="PIC/Admin" value={p.assigned_to_name} />
              <div className="py-1.5 border-b border-slate-100"><p className="text-xs text-slate-400">Status</p><span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs border ${PIPELINE_STATUS_CLS[p.status] || PIPELINE_STATUS_CLS.prospek_baru}`} data-testid="detail-status">{statusLabel(p.status)}</span></div>
              {p.status === "berhasil" && p.deal_date && <Row label="Tanggal Deal" value={p.deal_date} />}
              {p.status === "tidak_berhasil" && p.lost_reason && <Row label="Alasan Lost" value={p.lost_reason} />}
              <Row label={isL ? "Catatan Negosiasi" : "Catatan"} value={isL ? p.negotiation_notes : p.notes} />
            </div>
          </div>

          {p.chatbot_requests && p.chatbot_requests.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="chatbot-requests">
              <h2 className="font-semibold text-slate-900 mb-4">Riwayat Permintaan Chatbot</h2>
              <div className="space-y-3">
                {[...p.chatbot_requests].reverse().map((r, i) => (
                  <div key={i} className="border border-slate-100 rounded-lg p-3" data-testid={`chatbot-req-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">{r.program || "Permintaan info"}</span>
                      <span className="text-xs text-slate-400">{formatWIB(r.submitted_at)}</span>
                    </div>
                    {r.summary && <p className="text-xs text-slate-500 mt-1">{r.summary}</p>}
                    <p className="text-[11px] text-green-600 mt-1">{r.consent ? "✓ Bersedia dihubungi" : "Persetujuan tidak dicentang"} · Sumber: Chatbot Website</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Riwayat Follow-up</h2>
            {(!p.follow_ups || p.follow_ups.length === 0) ? (
              <p className="text-sm text-slate-400" data-testid="followup-empty">Belum ada follow-up.</p>
            ) : (
              <div className="space-y-3" data-testid="followup-list">
                {p.follow_ups.map((f) => (
                  <div key={f.id} className="border border-slate-100 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">{f.channel} · {FU_STATUS_LABELS[f.status] || f.status}</span>
                      <span className="text-xs text-slate-400">{formatWIB(f.scheduled_at)}</span>
                    </div>
                    {f.outcome && <p className="text-sm text-slate-600 mt-1">{f.outcome}</p>}
                    {f.notes && <p className="text-xs text-slate-500 mt-0.5">{f.notes}</p>}
                    {f.next_follow_up_at && <p className="text-xs text-[#f97316] mt-1">Berikutnya: {formatWIB(f.next_follow_up_at)}</p>}
                    {f.pic_name && <p className="text-[11px] text-slate-400 mt-1">PIC: {f.pic_name}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Riwayat Status</h2>
            {(!p.stage_history || p.stage_history.length === 0) ? (
              <p className="text-sm text-slate-400" data-testid="status-history-empty">Belum ada riwayat.</p>
            ) : (
              <div className="space-y-3" data-testid="status-history-list">
                {[...p.stage_history].reverse().map((h, i) => (
                  <div key={i} className="border border-slate-100 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">{statusLabel(h.status_lama) }{h.status_lama ? " → " : ""}<span className="text-[#0f2847]">{statusLabel(h.status_baru)}</span></span>
                      <span className="text-xs text-slate-400">{formatWIB(h.waktu)}</span>
                    </div>
                    {h.catatan && <p className="text-xs text-slate-500 mt-0.5">{h.catatan}</p>}
                    {h.user_name && <p className="text-[11px] text-slate-400 mt-1">Oleh: {h.user_name}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 h-fit">
          <h2 className="font-semibold text-slate-900 mb-4">Tambah Follow-up</h2>
          <form onSubmit={addFu} className="space-y-3" data-testid="followup-form">
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Tanggal & Jam</label><input type="datetime-local" className={inputCls} value={fu.scheduled_at} onChange={(e) => setFu({ ...fu, scheduled_at: e.target.value })} data-testid="fu-scheduled" required /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Kanal</label><select className={inputCls} value={fu.channel} onChange={(e) => setFu({ ...fu, channel: e.target.value })} data-testid="fu-channel">{CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Hasil Komunikasi</label><input className={inputCls} value={fu.outcome} onChange={(e) => setFu({ ...fu, outcome: e.target.value })} data-testid="fu-outcome" /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Catatan</label><textarea className={inputCls} rows={2} value={fu.notes} onChange={(e) => setFu({ ...fu, notes: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Follow-up Berikutnya</label><input type="datetime-local" className={inputCls} value={fu.next_follow_up_at} onChange={(e) => setFu({ ...fu, next_follow_up_at: e.target.value })} data-testid="fu-next" /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Status</label><select className={inputCls} value={fu.status} onChange={(e) => setFu({ ...fu, status: e.target.value })} data-testid="fu-status">{FU_STATUS.map((s) => <option key={s} value={s}>{FU_STATUS_LABELS[s]}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">PIC</label><select className={inputCls} value={fu.pic_user_id} onChange={(e) => setFu({ ...fu, pic_user_id: e.target.value })} data-testid="fu-pic">{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
            <button type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-[#0f2847] hover:bg-[#17416f] text-white font-medium px-4 py-2.5 rounded-lg" data-testid="fu-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Simpan Follow-up</button>
          </form>
        </div>
      </div>

      {showEdit && <ProspectForm mode="edit" initial={p} members={members} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
      {showStatus && <StatusChangeModal prospect={p} onClose={() => setShowStatus(false)} onSaved={() => { setShowStatus(false); load(); }} />}
      {showConvert && <ConvertModal mode="convert" type={isL ? "lembaga" : "individu"} prospect={p} members={members} onClose={() => setShowConvert(false)} onSaved={() => { setShowConvert(false); load(); }} />}
    </AppLayout>
  );
}
